/**
 * VAT from a known VAT amount, and invoices with several lines at different rates (B-0128). Pure; synced to the API
 * (/v1/converters/vat-gst). The existing add and remove maths (vatCalc in vatRates.ts) is NOT changed.
 *
 * Rounding on an invoice: VAT is worked out once per rate on that rate's subtotal and rounded to the cent (the way a VAT
 * summary on an invoice is laid out), then the rate groups are added. Quebec's GST and QST split is each charged on the net.
 */
import type { CalcStep } from "@/lib/calc/steps";

export const QC_GST_PCT = 5; // same figures as QUEBEC_GST_PCT / QUEBEC_QST_PCT in calcChecks.ts (a test keeps them equal)
export const QC_QST_PCT = 9.975;
export const QC_COMBINED_PCT = QC_GST_PCT + QC_QST_PCT;

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export interface FromVat { net: number; vat: number; gross: number; netMin: number; netMax: number }
/** Net and gross from a VAT amount at a rate. null when the rate is not above 0 (a VAT amount cannot say what it was charged on). */
export function vatFromAmount(vat: number, ratePct: number): FromVat | null {
  if (!Number.isFinite(vat) || !Number.isFinite(ratePct) || ratePct <= 0) return null;
  const rate = ratePct / 100;
  const net = vat / rate;
  // A receipt shows VAT rounded to the cent, so the true amount lies within half a cent either side.
  return { net, vat, gross: net + vat, netMin: Math.max(0, (vat - 0.005) / rate), netMax: (vat + 0.005) / rate };
}

export interface VatLine { amount: number; ratePct: number }
export interface RateGroup { ratePct: number; count: number; net: number; vat: number; gross: number }
export interface VatInvoice {
  groups: RateGroup[];
  net: number; vat: number; gross: number;
  /** Canada: GST and QST on the lines charged at the Quebec combined rate, when asked for. */
  quebec: { net: number; gst: number; qst: number } | null;
}

/** `inclusive` false: each amount is net (VAT added). true: each amount is gross (VAT taken out). */
export function vatInvoice(lines: VatLine[], inclusive: boolean, opts: { quebecSplit?: boolean } = {}): VatInvoice {
  const byRate = new Map<number, { count: number; sum: number }>();
  for (const l of lines) {
    const g = byRate.get(l.ratePct) ?? { count: 0, sum: 0 };
    g.count += 1; g.sum += l.amount;
    byRate.set(l.ratePct, g);
  }
  const groups: RateGroup[] = Array.from(byRate.entries()).sort((a, b) => b[0] - a[0]).map(([ratePct, g]) => {
    if (inclusive) {
      const gross = r2(g.sum), net = r2(gross / (1 + ratePct / 100));
      return { ratePct, count: g.count, net, vat: r2(gross - net), gross };
    }
    const net = r2(g.sum), vat = r2((net * ratePct) / 100);
    return { ratePct, count: g.count, net, vat, gross: r2(net + vat) };
  });
  const sum = (k: "net" | "vat" | "gross") => r2(groups.reduce((a, g) => a + g[k], 0));
  let quebec: VatInvoice["quebec"] = null;
  if (opts.quebecSplit) {
    const qcNet = r2(groups.filter((g) => Math.abs(g.ratePct - QC_COMBINED_PCT) < 1e-9).reduce((a, g) => a + g.net, 0));
    if (groups.some((g) => Math.abs(g.ratePct - QC_COMBINED_PCT) < 1e-9)) quebec = { net: qcNet, gst: r2((qcNet * QC_GST_PCT) / 100), qst: r2((qcNet * QC_QST_PCT) / 100) };
  }
  return { groups, net: sum("net"), vat: sum("vat"), gross: sum("gross"), quebec };
}

type Fmt = (n: number) => string | number;
export function vatFromAmountSteps(r: FromVat, ratePct: number, fmt: Fmt): CalcStep[] {
  return [
    { label: "Net amount", formula: "VAT amount / (rate / 100)", inputs: { "VAT amount": fmt(r.vat), Rate: `${ratePct}%` }, result: fmt(r.net) },
    { label: "Gross amount", formula: "Net amount + VAT amount", inputs: { "Net amount": fmt(r.net), "VAT amount": fmt(r.vat) }, result: fmt(r.gross) },
    { label: "Rounding range", formula: "A receipt rounds VAT to the cent, so use (VAT +/- 0.005) / rate", inputs: { "VAT amount": fmt(r.vat) }, result: `${fmt(r.netMin)} to ${fmt(r.netMax)} net` },
  ];
}
export function vatInvoiceSteps(inv: VatInvoice, inclusive: boolean, fmt: Fmt): CalcStep[] {
  const steps: CalcStep[] = inv.groups.map((g): CalcStep => inclusive
    ? { label: `Lines at ${g.ratePct}%`, formula: "Net = gross / (1 + rate); VAT = gross - net (per rate subtotal)", inputs: { Lines: g.count, "Gross subtotal": fmt(g.gross), Rate: `${g.ratePct}%` }, result: `net ${fmt(g.net)}, VAT ${fmt(g.vat)}` }
    : { label: `Lines at ${g.ratePct}%`, formula: "VAT = net subtotal x rate (per rate subtotal)", inputs: { Lines: g.count, "Net subtotal": fmt(g.net), Rate: `${g.ratePct}%` }, result: `VAT ${fmt(g.vat)}, gross ${fmt(g.gross)}` });
  steps.push({ label: "Invoice total", formula: "Add the rate groups", inputs: { Net: fmt(inv.net), VAT: fmt(inv.vat) }, result: fmt(inv.gross) });
  if (inv.quebec) steps.push({ label: "Quebec GST and QST", formula: `GST ${QC_GST_PCT}% and QST ${QC_QST_PCT}%, each on the net of the ${QC_COMBINED_PCT}% lines`, inputs: { Net: fmt(inv.quebec.net) }, result: `GST ${fmt(inv.quebec.gst)}, QST ${fmt(inv.quebec.qst)}` });
  return steps;
}
/** Working for the existing add / remove modes: the same numbers the page shows. */
export function vatSimpleSteps(amount: number, ratePct: number, inclusive: boolean, r: { net: number; vat: number; gross: number }, fmt: Fmt): CalcStep[] {
  return inclusive
    ? [
      { label: "Net amount", formula: "Gross amount / (1 + rate / 100)", inputs: { "Gross amount": fmt(amount), Rate: `${ratePct}%` }, result: fmt(r.net) },
      { label: "VAT", formula: "Gross amount - net amount", inputs: { "Gross amount": fmt(amount), "Net amount": fmt(r.net) }, result: fmt(r.vat) },
    ]
    : [
      { label: "VAT", formula: "Net amount x rate / 100", inputs: { "Net amount": fmt(amount), Rate: `${ratePct}%` }, result: fmt(r.vat) },
      { label: "Gross amount", formula: "Net amount + VAT", inputs: { "Net amount": fmt(amount), VAT: fmt(r.vat) }, result: fmt(r.gross) },
    ];
}
